import axios from "axios";
import { adoptCloudPoints, getLinkedTag, setLinkedTag, syncPointsToTag } from "./rewards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

/* module-level session cache + broadcast — any component can listen */
let cachedUser; // undefined = unchecked, null = guest, object = signed in

/* lightweight local flag so guest sessions never fire a doomed /auth/me call
   (the real session cookie is httpOnly — this just records that a login happened) */
const SESSION_FLAG = "wr-has-session";

const emit = () => window.dispatchEvent(new CustomEvent("dolo-auth", { detail: { user: cachedUser ?? null } }));

export const getCachedUser = () => cachedUser ?? null;

export const formatApiError = (detail, fallback = "SIGNAL LOST — RETRY.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

/* After any sign-in: the account's Titan Tag becomes the vault of record on
   this device — adopt its cloud points, then push the local stash up (the
   backend keeps the max, so nothing is ever lost). */
const afterAuth = (user) => {
  cachedUser = user;
  if (user) localStorage.setItem(SESSION_FLAG, "1");
  if (user?.tag) {
    if (getLinkedTag() !== user.tag) setLinkedTag(user.tag);
    adoptCloudPoints(user.tag)
      .then(() => syncPointsToTag(user.tag))
      .catch(() => {});
  }
  emit();
  return user;
};

export const fetchMe = async () => {
  if (!localStorage.getItem(SESSION_FLAG)) {
    cachedUser = null;
    return cachedUser;
  }
  try {
    const { data } = await client.get("/auth/me");
    cachedUser = data;
  } catch {
    cachedUser = null;
    localStorage.removeItem(SESSION_FLAG);
  }
  return cachedUser;
};

export const registerAccount = async (email, password, name) => {
  const { data } = await client.post("/auth/register", { email, password, name: name || undefined, tag: getLinkedTag() || undefined });
  return afterAuth(data);
};

export const loginAccount = async (email, password) => {
  const { data } = await client.post("/auth/login", { email, password, tag: getLinkedTag() || undefined });
  return afterAuth(data);
};

export const googleSession = async (sessionId) => {
  const { data } = await client.post("/auth/google/session", { session_id: sessionId, tag: getLinkedTag() || undefined });
  return afterAuth(data);
};

export const logoutAccount = async () => {
  await client.post("/auth/logout").catch(() => {});
  cachedUser = null;
  localStorage.removeItem(SESSION_FLAG);
  emit();
};

export const updateAccountName = async (name) => {
  const { data } = await client.patch("/auth/name", { name });
  cachedUser = data;
  emit();
  return data;
};

export const updatePreferences = async (dropAlerts) => {
  const { data } = await client.patch("/auth/preferences", { drop_alerts: dropAlerts });
  cachedUser = data;
  emit();
  return data;
};

export const alignFranchise = async (franchise) => {
  const { data } = await client.post("/franchise/align", { franchise });
  cachedUser = data;
  emit();
  return data;
};
